import { ReactElement, useEffect, useState } from "react";
import {
	Dialog,
	Form,
	FormItem,
	TextArea,
	ValidationError,
	TableView,
	DateString,
	Toolbar
} from "client/Components";
import {
	getRespondPreparation,
	RespondPreparationClientModel,
	respondToUserRequests,
	UserRequestClientModel,
	UserRequestRespondedClientModel,
	UserRequestRespondKind,
} from "client/Api/UserRequests/manageUserRequests";
import { localize } from "client_legacy/ndaw/culture";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { IFormHandler, useFormHandler } from "client/Forms";
import { RequestedItem } from "./RequestedItem";
import { getUserRequestKindText } from "./common";
import {
	AlertManager,
	useAlerts,
	useUserTimezone
} from "client/Hooks";
import { TimeZone } from "client/DateAndTime/timezones";
import { resolveFormattedTextString } from "client/Misc/utils";
import { FormattedTextString } from "client/Misc/commonTypes";

const {
	Row,
	Cell
} = TableView;

type FormModel = {
	note: string;
};

export type RespondToUserRequestDialogProps = {
	ids: readonly number[];
	kind: UserRequestRespondKind;
	onClose(): void;
	onResponded(reponse: readonly UserRequestRespondedClientModel[]): void;
};

export const RespondToUserRequestDialog = (props: RespondToUserRequestDialogProps): ReactElement => {
	const {
		ids,
		kind,
		onClose,
		onResponded
	} = props;

	const [isInteractingWithTheBackend, setIsInteractingWithTheBackend] = useState<boolean>(true);
	const [clientModel, setClientModel] = useState<RespondPreparationClientModel>();
	const alertManager: AlertManager = useAlerts();

	const [formModel, setFormModel] = useState<FormModel>({
		note: ""
	});

	const formHandler: IFormHandler<FormModel> = useFormHandler<FormModel>(formModel, setFormModel, "NoValidation");

	useEffect(
		() => {
			const load = async (
				ids: readonly number[],
				kind: UserRequestRespondKind,
				signal: AbortSignal
			): Promise<void> => {
				try {
					setIsInteractingWithTheBackend(true);
					const preparationModel: RespondPreparationClientModel = await getRespondPreparation(
						ids,
						kind,
						signal
					);
					setClientModel(preparationModel);
					setIsInteractingWithTheBackend(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					alertManager.showError(localize("_GeneralErrorMessage"));
					onClose();
				}
			};

			const cancellation = new AbortController();
			load(ids, kind, cancellation.signal);
			return () => cancellation.abort();
		},
		[alertManager, ids, kind, onClose]
	);

	const respondHandler = async (): Promise<void> => {
		const { note } = formHandler.reducer.value;

		try {
			setIsInteractingWithTheBackend(true);

			const response: readonly UserRequestRespondedClientModel[] = await respondToUserRequests({
				ids,
				kind,
				note
			});

			alertManager.showSuccess(
				localize(
					kind === UserRequestRespondKind.Accept
						? "_SuccessfullyAcceptedTheAccessRequests"
						: "_SuccessfullyDeclinedTheAccessRequests",
				)
			);

			onResponded(response);
		} catch (e) {
			if (!formHandler.validation.tryProcessServerError(e)) {
				alertManager.showError(localize("_GeneralErrorMessage"));
			}
			setIsInteractingWithTheBackend(false);
		}
	};

	if (clientModel && clientModel.isBlocked) {
		return (
			<ResponseBlockedDialog
				kind={kind}
				requests={clientModel.blockedItems}
				onClose={onClose}
			/>
		);
	}

	const noteLabel = (): FormattedTextString | undefined => {
		if (!clientModel) {
			return;
		}

		const result: string = clientModel.requesterNames.length === 1
			? resolveFormattedTextString({
				template: localize("_NoteToName"),
				replacements:[
					{ what: "{{Name}}", filler: clientModel.requesterNames[0] }
				]
			})
			: localize("_NoteToRequesters");

		return {
			template: localize("_OptionalLabelFormat"),
			replacements: [{ what: "{{Label}}", filler: result }]
		};
	};

	return (
		<Dialog
			trackingId="response-to-user-request-dialog"
			hasBusyIndicator={isInteractingWithTheBackend}
		>
			<Dialog.Header
				title={localize(
					kind === UserRequestRespondKind.Accept
						? (ids.length === 1 ? "_AcceptRequest" : "_AcceptRequests")
						: (ids.length === 1 ? "_DeclineRequest" : "_DeclineRequests")
				)}
				onClose={onClose}
			/>

			<Dialog.Body>
				{clientModel && (
					<Form onSubmit={respondHandler}>
						<FormItem
							label={noteLabel()}
						>
							<TextArea {...formHandler.for("note").attachToTextArea()} autoFocus />
							<ValidationError {...formHandler.for("note").attachToValidationError()} />
						</FormItem>
					</Form>
				)}
			</Dialog.Body>

			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize(
							kind === UserRequestRespondKind.Accept
								? "_Accept"
								: "_Decline"
						)}
						onClick={respondHandler}
						trackingId="decline"
					/>

					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
RespondToUserRequestDialog.dislayName = "RespondToUserRequestDialog";

type ResponseBlockedDialogProps = {
	kind: UserRequestRespondKind;
	requests: readonly UserRequestClientModel[];
	onClose(): void;
};

const ResponseBlockedDialog = (props: ResponseBlockedDialogProps): ReactElement => {
	const {
		kind,
		requests,
		onClose
	} = props;

	const userTimezone: TimeZone = useUserTimezone();

	return (
		<Dialog trackingId="response-to-user-request-blocked-dialog">
			<Dialog.Header
				title={localize(
					kind === UserRequestRespondKind.Accept
						? "_CantAcceptSelectedRequests"
						: "_CantDeclineSelectedRequests"
				)}
				onClose={onClose}
			/>

			<Dialog.Body>
				{localize(
					kind === UserRequestRespondKind.Accept
						? "_CantAcceptSelectedRequestsReason"
						: "_CantDeclineSelectedRequestsReason"
				)}

				<TableView
					trackingId="user-request-grid"
					items={requests}
					itemKey="id"
					columns={[
						{
							title: localize("_RequestedItem"),
							width: 200,
							trackingId: "requested-item"
						},
						{
							title: localize("_Type"),
							width: 200,
							trackingId: "type"
						},
						{
							title: localize("_RequestedBy"),
							width: 200,
							trackingId: "requested-by"
						},
						{
							title: localize("_Email"),
							width: 200,
							trackingId: "email"
						},
						{
							title: localize("_RequestDate"),
							width: 200,
							trackingId: "request-date"
						}
					]}
				>
					{request => (
						<Row trackingId={request.id.toString()}>
							<Cell>
								<RequestedItem item={request.item} />
							</Cell>
							<Cell>{getUserRequestKindText(request.item.kind)}</Cell>
							<Cell>{request.requester.name}</Cell>
							<Cell>{request.requester.email}</Cell>
							<Cell>
								<DateString
									value={request.createdOn}
									format="DMYT"
									timezone={userTimezone}
								/>
							</Cell>
						</Row>
					)}
				</TableView>
			</Dialog.Body>

			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="secondary"
						text={localize("_Close")}
						onClick={onClose}
						trackingId="close"
						responsiveBehavior="hide"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
ResponseBlockedDialog.displayName = "ResponseBlockedDialog";